import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

const MOOD_LABELS = ["heavy", "tender", "tired", "quiet", "steady", "steady", "curious", "hopeful", "light", "alive"];

export default function DailyCheckIn({ open, onOpenChange }) {
    const [mood, setMood] = useState([6]);
    const [notes, setNotes] = useState("");
    const [intention, setIntention] = useState("");
    const [existingIntention, setExistingIntention] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            api.get("/intentions/today").then((r) => {
                setExistingIntention(r.data);
                if (r.data) setIntention(r.data.text);
            }).catch(() => {});
        }
    }, [open]);

    const moodLabel = MOOD_LABELS[mood[0] - 1] || "steady";

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post("/mood-logs", {
                mood_score: mood[0],
                mood_label: moodLabel,
                notes: notes || null,
            });
            if (intention.trim() && (!existingIntention || existingIntention.text !== intention.trim())) {
                await api.post("/intentions", { text: intention.trim() });
            }
            toast.success("Held. Come back whenever you need.");
            onOpenChange(false);
            setNotes("");
        } catch (e) {
            toast.error("Something didn't save. Try again in a moment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg bg-brand-cream border-brand-darkPurple/10" data-testid="daily-checkin-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-3xl text-brand-darkPurple flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-brand-spark" strokeWidth={1.5} />
                        Today's soft check-in
                    </DialogTitle>
                    <DialogDescription className="text-brand-charcoal/70">
                        No right answers. Just a small pause.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-7 pt-2">
                    <div>
                        <label className="block text-sm text-brand-darkPurple mb-3">How does today feel in your body?</label>
                        <Slider
                            data-testid="mood-slider"
                            min={1} max={10} step={1}
                            value={mood}
                            onValueChange={setMood}
                            className="[&_[role=slider]]:bg-brand-gold [&_[role=slider]]:border-brand-gold [&_.bg-primary]:bg-brand-gold"
                        />
                        <div className="mt-3 flex items-center justify-between text-xs text-brand-charcoal/60">
                            <span>heavy</span>
                            <span className="font-serif italic text-lg text-brand-darkPurple">{moodLabel}</span>
                            <span>alive</span>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm text-brand-darkPurple mb-2">What wants to be witnessed? <span className="text-brand-charcoal/50">(optional)</span></label>
                        <Textarea
                            data-testid="checkin-notes-input"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="A word, a sentence, a fragment — whatever's true right now."
                            className="min-h-[70px] bg-white border-brand-darkPurple/10 focus-visible:ring-brand-gold/50 resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-brand-darkPurple mb-2">One intention for today</label>
                        <Textarea
                            data-testid="checkin-intention-input"
                            value={intention}
                            onChange={(e) => setIntention(e.target.value)}
                            placeholder="e.g., be gentle with yourself when the meeting ends"
                            className="min-h-[60px] bg-white border-brand-darkPurple/10 focus-visible:ring-brand-gold/50 resize-none"
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            className="text-brand-charcoal hover:bg-brand-darkPurple/5 rounded-full"
                            data-testid="checkin-cancel-btn"
                        >
                            Not now
                        </Button>
                        <Button
                            data-testid="checkin-submit-btn"
                            onClick={submit}
                            disabled={submitting}
                            className="bg-brand-darkPurple hover:bg-brand-darkPurpleSoft text-white rounded-full px-6"
                        >
                            {submitting ? "Holding…" : "Set the day"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
